import api, { buildQueryParams, normalizeList } from './api';

// Service Types
export const getServiceTypes = async () => {
    const response = await api.get('/api/service-orders/service-types/');
    return normalizeList(response.data);
};

export const getServiceType = async (id) => {
    const response = await api.get(`/api/service-orders/service-types/${id}/`);
    return response.data;
};

export const createServiceType = async (data) => {
    const response = await api.post('/api/service-orders/service-types/', data);
    return response.data;
};

export const updateServiceType = async (id, data) => {
    const response = await api.put(`/api/service-orders/types/${id}/`, data);
    return response.data;
};

export const deleteServiceType = async (id) => {
    await api.delete(`/api/service-orders/types/${id}/`);
};

// Service Orders
export const getServiceOrders = async (params = {}) => {
    try {
        const queryString = buildQueryParams(params);
        const url = queryString ? `/api/service-orders/service-orders/?${queryString}` : '/api/service-orders/service-orders/';
        const response = await api.get(url);
        
        return response.data;
    } catch (error) {
        console.error('Error in getServiceOrders:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

/**
 * Fetch all service orders by requesting every page (for dropdowns that need the full list).
 * Uses page_size=50 (backend max for this endpoint) and follows next until done.
 */
export const getAllServiceOrders = async (params = {}) => {
    const pageSize = 50;
    const allResults = [];
    let page = 1;
    let hasNext = true;

    while (hasNext) {
        const response = await getServiceOrders({
            ...params,
            page_size: pageSize,
            page,
        });
        const results = response?.results ?? response;
        const list = Array.isArray(results) ? results : [];
        allResults.push(...list);
        hasNext = Boolean(response?.next);
        page += 1;
        if (list.length < pageSize) break;
    }

    return allResults;
};

export const getServiceOrder = async (id) => {
    try {
        // Use a longer timeout for this call since it loads a lot of related data
        // (workflow details, technician credentials, equipment, consumables, etc.)
        const response = await api.get(`/api/service-orders/service-orders/${id}/?include_workflow_details=true&include_technician_credentials=true`, {
            timeout: 60000 // 60 seconds - increased from default 30s due to heavy data loading
        });
        return response.data;
    } catch (error) {
        console.error('Error in getServiceOrder:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
            if (error.code === 'ECONNABORTED') {
                console.error('⚠️ Request timed out after 60 seconds. The backend may be slow or the service order has too much related data.');
            }
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const createServiceOrder = async (data) => {
    try {
        const response = await api.post('/api/service-orders/service-orders/', data);
        return response.data;
    } catch (error) {
        console.error('Error in createServiceOrder:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const updateServiceOrder = async (id, data) => {
    try {
        const response = await api.put(`/api/service-orders/service-orders/${id}/`, data);
        return response.data;
    } catch (error) {
        console.error('Error in updateServiceOrder:', error);
        if (error.response) {
            console.error('Error response:', error.response.data);
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        throw error;
    }
};

export const deleteServiceOrder = async (id) => {
    const response = await api.delete(`/api/service-orders/service-orders/${id}/`);
    return response.data;
};

// Service Order Actions
export const addConsumable = async (orderId, data) => {
    const response = await api.post(`/api/service-orders/service-orders/${orderId}/add_consumable/`, data);
    return response.data;
};

export const updateStatus = async (orderId, status) => {
    const response = await api.post(`/api/service-orders/service-orders/${orderId}/update_status/`, { status });
    return response.data;
};

export const addConsumableToServiceOrder = async (id, data) => {
    const response = await api.post(`/api/service-orders/service-orders/${id}/add_consumable/`, data);
    return response.data;
};

export const updateServiceOrderStatus = async (id, status) => {
    const response = await api.post(`/api/service-orders/service-orders/${id}/update_status/`, { status });
    return response.data;
};

export const getServiceOrderPriorities = async () => {
    const response = await api.get('/api/service-orders/service-order-priorities/');
    return normalizeList(response.data);
};

export const getServiceOrderStatuses = async () => {
    const response = await api.get('/api/service-orders/service-order-statuses/');
    return normalizeList(response.data);
};

export const getAvailableTransitions = async (id) => {
    return api.get(`/api/service-orders/service-orders/${id}/available_transitions/`);
};

export const checkTransitionImpact = async (id, to_state_id) => {
    return api.get(`/api/service-orders/service-orders/${id}/check_transition_impact/?to_state_id=${to_state_id}`);
};

export const transitionServiceOrder = async (
    id,
    to_state_id,
    notes = '',
    quotation_document_id = null,
    send_quotation_email = false,
) => {
    const payload = { to_state_id, notes, send_quotation_email: !!send_quotation_email };
    if (quotation_document_id) {
        payload.quotation_document_id = quotation_document_id;
    }
    return api.post(`/api/service-orders/service-orders/${id}/transition/`, payload);
};

export const checkCanComplete = async (id) => {
    return api.get(`/api/service-orders/service-orders/${id}/can_complete/`);
};

export const serviceOrderService = {
    getAll: () => api.get('/api/service-orders/service-orders/'),
    getById: (id) => api.get(`/api/service-orders/service-orders/${id}/`),
    create: (data) => api.post('/api/service-orders/service-orders/', data),
    update: (id, data) => api.put(`/api/service-orders/service-orders/${id}/`, data),
    delete: (id) => api.delete(`/api/service-orders/service-orders/${id}/`),
    updateStatus: (id, status) => api.post(`/api/service-orders/service-orders/${id}/update_status/`, { status }),
    addConsumable: (id, data) => api.post(`/api/service-orders/service-orders/${id}/add_consumable/`, data),
    
    // Equipment management
    getEquipment: async (id) => {
        const response = await api.get(`/api/service-orders/service-orders/${id}/equipment/`);
        return response.data;
    },
    assignEquipment: async (id, equipmentIds) => {
        const response = await api.post(`/api/service-orders/service-orders/${id}/assign_equipment/`, { equipment_ids: equipmentIds });
        return response.data;
    },
    removeEquipment: async (id, equipmentId) => {
        const response = await api.delete(`/api/service-orders/service-orders/${id}/remove_equipment/?equipment_id=${equipmentId}`);
        return response.data;
    },
    
    // Consumables management
    getConsumables: async (id) => {
        const response = await api.get(`/api/service-orders/service-orders/${id}/consumables/`);
        return response.data;
    },
    updateConsumable: async (id, consumableId, quantity) => {
        const response = await api.put(`/api/service-orders/service-orders/${id}/update_consumable/`, { consumable_id: consumableId, quantity });
        return response.data;
    },
    removeConsumable: async (id, consumableId) => {
        const response = await api.delete(`/api/service-orders/service-orders/${id}/remove_consumable/?consumable_id=${consumableId}`);
        return response.data;
    },
    
    // Sub-service orders management
    getSubServiceOrders: async (id) => {
        const response = await api.get(`/api/service-orders/service-orders/${id}/sub_service_orders/`);
        return response.data;
    },
    createSubServiceOrder: async (id, data) => {
        const response = await api.post(`/api/service-orders/service-orders/${id}/create_sub_service_order/`, data);
        return response.data;
    },
    getParentOrder: async (id) => {
        const response = await api.get(`/api/service-orders/service-orders/${id}/parent_order/`);
        return response.data;
    },
};

// Sub-service order helper functions
export const getSubServiceOrders = async (id) => {
    const response = await api.get(`/api/service-orders/service-orders/${id}/sub_service_orders/`);
    return response.data;
};

export const createSubServiceOrder = async (parentId, data) => {
    const response = await api.post(`/api/service-orders/service-orders/${parentId}/create_sub_service_order/`, data);
    return response.data;
};

export const getParentOrder = async (id) => {
    const response = await api.get(`/api/service-orders/service-orders/${id}/parent_order/`);
    return response.data;
}; 